import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { cloneDeep } from 'lodash';
import { useComponent } from '../../../UserSetting/UsersLibrary/UserRights';
import { pageSize } from '../../../../Components/helper';
import * as action from '../store/UserInstruction.action';

export const CareUserContext = createContext(null);

export default function CareUserContextProvider({ children }) {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');
  const { Users, UserInstructionDetails } = useSelector((state) => state);
  const { userInstructionList, totalRecords, columns, allColumns, tabData, careUserGroupList, groupUserInstructionDetails } = UserInstructionDetails;
  const { logInRights } = Users;
  const { CheckActionPermission } = useComponent();
  const CareAddEditAccess = CheckActionPermission(logInRights, 'libraries', 'care', 'addEdit');
  const CareDeleteAccess = CheckActionPermission(logInRights, 'libraries', 'care', 'delete');
  const { _id } = useParams();

  const initialSetting = {
    UserInstructionList: [],
    pageIndex: 1,
    pageSize,
    hasMore: true,
    loading: true,
    totalRecords: totalRecords || 0,
    searchText: '',
    searchLoaded: false,
    deleteId: '',
    showConfirmationPopup: false,
    selectAll: false,
    editId: '',
    name: '',
    errors: {},
    showTabModal: false,
    getName: '',
    selectedIds: [],
    showColumnConfig: false,
    columns: [],
    addColumns: [],
    editMode: false,
    tabData: [],
    showAddEditColumn: false,
    columnLoading: false,
    selectedTabId: _id || '',
    tabName: '',
    isSelectAll: false,
    selectedIdArray: [],
    showLoader: false,
    columnModalLoading: false,
    filesSelected: false,
    checked: false,
    confirmUploadProcess: false,
    showErrModal: false,
    addNewRow: false,
    totalRecordsToUpload: 0,
    errorRecordCount: 0,
    successRecordCount: 0,
    errFile: '',
    editingRowId: '',
    fromTab: false,
    careUserGroups: [],
    showImageUpload: false,
    careGroupDetails: {
      name: '',
      productType: [],
      brandIds: [],
      catOneIds: [],
      catTwoIds: [],
      catThreeIds: [],
      catFourIds: [],
      error: false,
      additonalInformation: '',
      tabs: [],
      isSaveDisable: false,
      subBrandIds: [],
    },
    deleteColumnId: '',
    selectedImageRowIndex: '',
    selectedImageRowColumnId: '',
    rowImage: null,
    showImgDeleteModal: false,
    careUserGroupList: [],
    deleteGroupId: '',
    showDragDrop: false,
    expandTabId: '',
    accordianArray: [],
    switchKey: false,
    fromGroup: false,
    isSelectGroup: {},
    updatedTabDetails: [],
  };
  const [state, setState] = useState(initialSetting);
  const [tabPageLoading, setTabPageLoading] = useState(false);

  useEffect(() => {
    if (groupUserInstructionDetails) {
      setState((prevState) => ({
        ...prevState,
        careGroupDetails: {
          name: groupUserInstructionDetails?.name,
          productType: groupUserInstructionDetails?.productType,
          brandIds: groupUserInstructionDetails?.brandIds,
          catOneIds: groupUserInstructionDetails?.catOneIds,
          catTwoIds: groupUserInstructionDetails?.catTwoIds,
          catThreeIds: groupUserInstructionDetails?.catThreeIds,
          catFourIds: groupUserInstructionDetails?.catFourIds,
          additonalInformation: groupUserInstructionDetails?.additonalInformation,
          isRequired: groupUserInstructionDetails?.isRequired,
          tabs: groupUserInstructionDetails?.tabs || [], // Assign the modified tabs array without tabColumns
          subBrandIds: groupUserInstructionDetails?.subBrandIds,
        },
      }));
    }
  }, [groupUserInstructionDetails]);

  useEffect(() => {
    if (Array.isArray(allColumns)) {
      setState((prevState) => ({
        ...prevState,
        careGroupDetails: {
          ...prevState?.careGroupDetails,
          tabs: prevState?.careGroupDetails?.tabs.map((tab) => {
            // Find the columns in allColumns where the tabId matches tab._id
            const matchingColumns = allColumns.find((column) => column.tabId === tab._id)?.columns || [];

            return {
              ...tab, // Spread the existing tab properties to preserve them
              tabColumns: matchingColumns, // Assign the matching columns to tabColumns
            };
          }),
        },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allColumns]);

  const getUserInstructionType = async () => {
    const params = {};
    if (state?.searchText) {
      params.searchText = state?.searchText;
    }
    setState((prevState) => ({
      ...prevState,
      loading: true,
    }));
    await dispatch(action.fetchUserInstructionAction(params, t));
    setState((prevState) => ({
      ...prevState,
      loading: false,
    }));
  };

  const getGroupTabs = async () => {
    const params = {};
    if (state?.searchText) {
      params.searchText = state?.searchText;
    }
    setState((prevState) => ({
      ...prevState,
      loading: true,
    }));
    await dispatch(action.fetchUserInstructionCareGroupAction(params));
    setState((prevState) => ({
      ...prevState,
      loading: false,
    }));
  };

  const validateValues = (name) => {
    const errors = {};
    if (!name.trim()) {
      errors.name = 'Name is required';
    }
    return errors;
  };

  const handleRemaining = (a) => {
    return a.forDocument ? 1 : -1;
  };

  useEffect(() => {
    if (Array.isArray(userInstructionList) && Array.isArray(allColumns)) {
      const tabsData = userInstructionList
        .filter((ele) => !ele?.forDocument) // Filters out elements where forDocument is false
        .map((ele) => {
          // Find the columns in allColumns where the tabId matches tab._id
          const matchingColumns = allColumns.filter((column) => column.tabId === ele._id) || [];
          return {
            ...ele, // Spread the existing tab properties to preserve them
            tabColumns: matchingColumns, // Assign the matching columns to tabColumns
            tabId: ele?._id,
            tabName: ele?.name,
          };
        }); // Adds new keys

      setState((prevState) => ({
        ...prevState,
        UserInstructionList: userInstructionList?.sort((a, b) => (a.forDocument === b.forDocument ? 0 : handleRemaining(a))),
        totalRecords: totalRecords !== null ? totalRecords : userInstructionList?.length,
        hasMore: totalRecords > userInstructionList?.length,
        careGroupDetails: {
          ...prevState?.careGroupDetails,
          tabs: tabsData,
        },
      }));
    }
  }, [userInstructionList, allColumns, totalRecords]);

  useEffect(() => {
    if (Array.isArray(columns)) {
      setState((prevState) => ({
        ...prevState,
        columns,
        careUserGroupList,
      }));
    }
  }, [columns, careUserGroupList]);

  useEffect(() => {
    if (Array.isArray(tabData)) {
      const groupedData = tabData?.reduce((acc, item) => {
        acc[item?.rowIndex] = acc[item?.rowIndex] || [];
        acc[item?.rowIndex]?.push(item);
        return acc;
      }, {});

      const transformedTabData = Object?.values(groupedData);
      setState((prevState) => ({
        ...prevState,
        tabData: transformedTabData,
      }));
    }
  }, [tabData]);

  const handleSearch = (e, fromTab, fromGroup) => {
    const targetValue = e?.target?.value;
    setState((prevState) => ({
      ...prevState,
      searchText: targetValue,
      searchLoaded: true,
      fromTab,
      fromGroup,
    }));
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (state?.searchLoaded) {
        if (state?.fromTab) {
          setState((prevState) => ({
            ...prevState,
            loading: true,
            pageIndex: 1,
            tabData: [],
            hasMore: false,
            showLoader: true,
          }));
          await dispatch(action.clearTabDetailsReducer());
          await dispatch(action.getAllUserTabs(_id, { searchText: state?.searchText }));
        } else if (state?.fromGroup) {
          setState((prevState) => ({
            ...prevState,
            loading: true,
            pageIndex: 1,
            groupUserInstructionDetails: [],
            hasMore: false,
          }));
          await dispatch(action.clearTabReducer());
          await getGroupTabs();
        } else {
          setState((prevState) => ({
            ...prevState,
            loading: true,
            pageIndex: 1,
            UserInstructionList: [],
            hasMore: true,
          }));
          await dispatch(action.clearTabReducer());
          await getUserInstructionType();
        }

        setState((prevState) => ({
          ...prevState,
          loading: false,
          searchLoaded: false,
          showLoader: false,
        }));
      }
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.searchText]);

  const validateColumns = () => {
    const errors = [];
    const updatedColumns = state?.addColumns?.map((column, columnIndex) => {
      const columnError = { hasError: false, errorMessage: '' };
      if (!column?.name?.trim()) {
        columnError.hasError = true;
        columnError.errorMessage = t('PIM_MODULE.COLUMN_MODAL.ADD_EDIT_COLUMN.NAME_IS_REQUIRED');
      } else if (!column?.columnType) {
        columnError.hasError = true;
        columnError.errorMessage = t('PIM_MODULE.COLUMN_MODAL.ADD_EDIT_COLUMN.COLUMN_TYPE_REQUIRED');
      } else if (column?.columnType?.key === 'Textbox' && !column?.characterLimit) {
        columnError.hasError = true;
        columnError.errorMessage = t('PIM_MODULE.COLUMN_MODAL.ADD_EDIT_COLUMN.CHARACTER_LIMIT_REQUIRED');
      } else if (['Checkbox', 'Dropdown', 'Multi_select_Dropdown', 'Radio_button'].includes(column?.columnType?.key)) {
        if (!column?.options || column?.options.some((option) => !option?.name?.trim())) {
          columnError.hasError = true;
          columnError.errorMessage = t('PIM_MODULE.COLUMN_MODAL.ADD_EDIT_COLUMN.OPTIONS_REQUIRED');
        }
      } else if (['Number_Decimal', 'Number_percentage'].includes(column?.columnType?.key)) {
        const hasColourError = column?.colourArray?.some(
          (colour) => (colour?.colourValue && !colour?.startPercentage) || (colour?.colourValue?._id === 'between' && !colour?.endPercentage),
        );
        columnError.hasError = hasColourError;
        columnError.errorMessage = t('PIM_MODULE.COLUMN_MODAL.ADD_EDIT_COLUMN.OPTIONS_REQUIRED');
      }
      errors[columnIndex] = columnError;
      return {
        ...column,
        hasError: columnError?.hasError,
        errorMessage: columnError?.errorMessage,
      };
    });
    setState((prevState) => ({
      ...prevState,
      addColumns: updatedColumns,
    }));
    return errors?.some((err) => err?.hasError);
  };

  const rows = state?.tabData;

  const handleSelectAll = () => {
    let SelectedIds = [];
    if (!state?.isSelectAll) {
      SelectedIds = Object?.values(rows)?.map((arr) => arr[0]?.rowId);
    } else {
      SelectedIds = [];
    }
    setState((prevState) => ({
      ...prevState,
      selectedIdArray: SelectedIds,
      isSelectAll: !state?.isSelectAll,
    }));
  };

  const handleRowSelection = (rowId) => {
    let updatedSelectedIds = [...state.selectedIdArray];

    if (updatedSelectedIds?.includes(rowId)) {
      updatedSelectedIds = updatedSelectedIds?.filter((id) => id !== rowId);
    } else {
      updatedSelectedIds?.push(rowId);
    }
    const allRowsSelected = updatedSelectedIds?.length === Object?.keys(rows)?.length;
    setState((prevState) => ({
      ...prevState,
      selectedIdArray: updatedSelectedIds,
      isSelectAll: allRowsSelected,
    }));
  };
  useEffect(() => {
    if (Array.isArray(userInstructionList)) {
      setState((prevState) => ({
        ...prevState,
        UserInstructionList: userInstructionList,
        totalRecords: totalRecords !== null ? totalRecords : userInstructionList?.length,
        hasMore: totalRecords > userInstructionList?.length,
      }));
    }
  }, [userInstructionList, totalRecords]);

  const fetchTabData = async () => {
    setState((prevState) => ({
      ...prevState,
      showLoader: true,
    }));
    const item = state?.UserInstructionList?.find((element) => element?._id === _id);
    if (item) {
      setState((prevState) => ({
        ...prevState,
        title: item?.name,
      }));
    } else {
      getUserInstructionType();
    }
    await dispatch(action.fetchColumnDetailList(_id));
    await dispatch(action.getAllUserTabs(_id));
    setState((prevState) => ({
      ...prevState,
      showLoader: false,
    }));
  };

  useEffect(() => {
    if (_id) fetchTabData();
  }, [dispatch, _id]);

  const contextValue = useMemo(() => {
    const onSubmitMethod = async () => {
      const newName = (state?.name || '')?.trim();
      const errors = validateValues(newName);
      if (Object.keys(errors)?.length > 0) {
        setState((prevState) => ({
          ...prevState,
          errors,
        }));
        return;
      }
      let res = false;
      setTabPageLoading(true);
      setState((prevState) => ({
        ...prevState,
        showLoader: true,
      }));
      if (state?.editId) {
        res = await dispatch(action.updateUserInstructionAction({ name: newName, _id: state?.editId }, t));
      } else {
        res = await dispatch(action.addUserInstructionAction({ name: newName }, t));
      }
      if (res) {
        dispatch(action.clearTabReducer());
        getUserInstructionType();
      }
      setState((prevState) => ({
        ...prevState,
        showTabModal: false,
        name: '',
        editId: '',
        errors: {},
        showLoader: false,
      }));
      setTabPageLoading(false);
    };

    const handleDeleteTabData = (id, multipleDelete) => {
      const selectedIds = state?.selectedIdArray; // Get the selected rowIds from state

      const data = cloneDeep(state?.careGroupDetails);
      if (multipleDelete && selectedIds?.length > 0) {
        const updatedTabs = data?.tabs.map((tab) => {
          // Filter out all matching rowIds from tabDetails
          const updatedTabDetails = tab?.tabDetails?.filter(
            (detail) => !selectedIds.includes(detail.rowId), // Remove any rowId that is in selectedIdArray
          );
          // Return the tab with updated tabDetails
          return {
            ...tab,
            tabDetails: updatedTabDetails,
          };
        });

        // Update the state with updated tabs and reset selectedIdArray
        setState((prevState) => ({
          ...prevState,
          careGroupDetails: {
            ...prevState.careGroupDetails,
            tabs: updatedTabs,
          },
          selectedIdArray: [], // Reset selectedIdArray after deletion
          isSelectGroup: {}, // Reset select all checkbox
        }));
      } else if (!multipleDelete && id !== '') {
        const updatedTabs = data?.tabs.map((tab) => {
          // Filter out all matching rowIds from tabDetails
          const updatedTabDetails = tab?.tabDetails?.filter(
            (detail) => detail.rowId !== id, // Remove any rowId that is in selectedIdArray
          );
          // Return the tab with updated tabDetails
          return {
            ...tab,
            tabDetails: updatedTabDetails,
          };
        });

        // Update the state with updated tabs and reset selectedIdArray
        setState((prevState) => ({
          ...prevState,
          careGroupDetails: {
            ...prevState.careGroupDetails,
            tabs: updatedTabs,
          },
          selectedIdArray: [], // Reset selectedIdArray after deletion
          isSelectGroup: {}, // Reset select all checkbox
        }));
      } else if (multipleDelete && selectedIds?.length === 0) {
        console.log('No rows selected for deletion.');
      }
    };

    const handleDeleteMultiple = (id) => {
      if (id) {
        setState((prevState) => ({
          ...prevState,
          deleteId: id,
        }));
      }
      setState((prevState) => ({
        ...prevState,
        showConfirmationPopup: true,
      }));
    };

    const handleRefresh = async (groupId) => {
      await dispatch(action.refreshTabs(groupId, t));
    };

    return {
      state,
      setState,
      CareAddEditAccess,
      CareDeleteAccess,
      onSubmitMethod,
      getUserInstructionType,
      handleSearch,
      validateValues,
      validateColumns,
      handleSelectAll,
      handleRowSelection,
      rows,
      handleDeleteMultiple,
      handleRefresh,
      fetchTabData,
      getGroupTabs,
      tabPageLoading,
      handleDeleteTabData,
    };
  }, [state, setState, rows, tabPageLoading, CareAddEditAccess, CareDeleteAccess, getUserInstructionType, handleSearch, validateValues, fetchTabData]);

  return <CareUserContext.Provider value={contextValue}>{children}</CareUserContext.Provider>;
}

export function useCareUserContext() {
  const context = useContext(CareUserContext);
  if (!context) {
    throw new Error('useCareUserContext must be used within a CareUserContextProvider');
  }
  return context;
}
